import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import CommonDialog from "../../../../common/components/dialog/CommonDialog";
import Select, { SelectItem } from "../../../../common/components/new/select/Select";
import Input from "../../../../common/components/new/ui/Input";
import styles from "./addressForm.module.scss";
import { State } from "../../../../model/state";
import { useLazyGetStatesQuery } from "../../../../service/state-service";
import { toast } from "react-toastify";
import { useCreateDeliveryAddressMutation, useUpdateDeliveryAddressMutation } from "../../../../service/delivery-address-service";
import { DeliveryAddress } from "../../../../model/delivery-address";
import { hideLoader, showLoader } from "../../../../stores/slices/loader-slice";
import { useDispatch } from "react-redux";
import { USER_BILL_TYPE } from "../type";
import { addDeliveryAddress, updateDeliveryAddress } from "../../../../stores/slices/guest-delivery-address-slice";
import AddressFormPhone from "../../../../common/components/new/addressPhoneNumber/addressFormPhoneNumber";

interface IProps {
	isEdit: boolean;
	addressToEdit?: DeliveryAddress;
	handleCancel: () => void;
	bilType: USER_BILL_TYPE | undefined;
}

const AddressForm = ({ isEdit, addressToEdit, handleCancel, bilType }: IProps) => {
	const dispatch = useDispatch();
	type FormValues = {
		firstName: string;
		lastName: string;
		phoneNumber: string;
		additionalPhoneNumber?: string;
		emailAddress: string;
		deliveryAddress: string;
		additionalInformation?: string;
		state: SelectItem;
		city: string;
	};
	const [states, setStates] = useState<State[]>();
	const [getStates] = useLazyGetStatesQuery();
	const [createAddress, { isLoading: createLoading }] = useCreateDeliveryAddressMutation();
	const [editAddress, { isLoading: isEditLoading }] = useUpdateDeliveryAddressMutation();
	const fetchStates = useCallback(async () => {
		try {
			let response = await getStates().unwrap();
			setStates(response);
		} catch (error) {
			toast.error("Error loading States");
		}
	}, []);

	const handleCreateAddress = (address: DeliveryAddress) => {
		if (bilType === USER_BILL_TYPE.CUSTOMER) {
			dispatch(showLoader());
			createAddress(address)
				.unwrap()
				.then(res => {
					toast.success("Delivery Address created successful");
					handleCancel();
				})
				.catch(err => toast.error(err?.data?.title as string))
				.finally(() => dispatch(hideLoader()));
		}
		if (bilType === USER_BILL_TYPE.GUEST) {
			dispatch(addDeliveryAddress(address));
			toast.success("Delivery Address created successful");
			handleCancel();
		}
	};

	const handleEditDeliveryAddress = (address: DeliveryAddress) => {
		if (bilType === USER_BILL_TYPE.CUSTOMER) {
			dispatch(showLoader());
			editAddress(address)
				.unwrap()
				.then(res => {
					toast.success("Delivery Address edit successful");
					handleCancel();
				})
				.catch(err => toast.error(err?.data?.title as string))
				.finally(() => dispatch(hideLoader()));
		}

		if (bilType === USER_BILL_TYPE.GUEST) {
			dispatch(updateDeliveryAddress(address));
			toast.success("Delivery Address edit successful");
			handleCancel();
		}
	};

	const stateItems = states?.map(state => new SelectItem(state.id, state.name)) || [];

	useEffect(() => {
		fetchStates();
	}, []);

	useEffect(() => {
		if (addressToEdit && stateItems.length > 0) {
			const selectedState = stateItems.find(x => x.label === addressToEdit?.deliveryAddressInformation?.region);
			if (selectedState) {
				setValue("state", selectedState);
			}
		}
	}, [stateItems, addressToEdit]);

	const {
		register,
		handleSubmit,
		control,
		watch,
		setValue,
		formState: { errors },
		trigger,
	} = useForm<FormValues>({
		reValidateMode: "onChange",
		mode: "onChange",
		defaultValues: {
			firstName: addressToEdit?.deliveryAddressInformation?.firstName,
			lastName: addressToEdit?.deliveryAddressInformation?.lastName,
			phoneNumber: addressToEdit?.deliveryAddressInformation?.phoneNumber,
			additionalPhoneNumber: addressToEdit?.deliveryAddressInformation?.additionalPhoneNumber,
			deliveryAddress: addressToEdit?.deliveryAddressInformation?.address,
			additionalInformation: addressToEdit?.deliveryAddressInformation?.additionalInformation,
			state: stateItems.find(x => x.label == addressToEdit?.deliveryAddressInformation?.region),
			city: addressToEdit?.deliveryAddressInformation?.city,
		},
	});

	const onSubmit: SubmitHandler<FormValues> = data => {
		if (!isEdit) {
			let delivery = {
				id: "",
				deliveryAddressInformation: {
					firstName: data?.firstName,
					lastName: data?.lastName,
					phoneNumber: data?.phoneNumber,
					additionalPhoneNumber: data?.additionalPhoneNumber,
					address: data?.deliveryAddress,
					additionalInformation: data?.additionalInformation,
					region: data?.state?.label,
					city: data?.city,
				},
				isDefault: false,
			} as DeliveryAddress;
			handleCreateAddress(delivery);
			return;
		}

		if (isEdit) {
			let delivery = {
				deliveryAddressId: addressToEdit?.id,
				id: addressToEdit?.id,
				deliveryAddressInformation: {
					firstName: data?.firstName,
					lastName: data?.lastName,
					phoneNumber: data?.phoneNumber,
					additionalPhoneNumber: data?.additionalPhoneNumber,
					address: data?.deliveryAddress,
					additionalInformation: data?.additionalInformation,
					region: data?.state?.label,
					city: data?.city,
				},
				isDefault: addressToEdit?.isDefault,
			} as DeliveryAddress;
			
			 handleEditDeliveryAddress(delivery);
		}
	};

	const registeredInputs = useMemo(() => {
		return {
			firstName: register("firstName", { required: "First name is required" }),
			lastName: register("lastName", { required: "Last name is required" }),

			deliveryAddress: register("deliveryAddress", { required: "Address is required" }),
			city: register("city", { required: "City is required" }),
			region: register("state", { required: "State is required" }),
			additionalInformation: register("additionalInformation"),
		};
	}, [register, watch]);

	return (
		<CommonDialog>
			<div className={styles.container}>
				<h2 className={styles.headerText}>{!isEdit ? "Add Address" : "Edit Address"}</h2>
				<div className={styles.form}>
					<div className={styles.inputGroup}>
						<div style={{ flex: 1 }}>
							<Input
								isIcon={false}
								label="First name"
								placeholder=""
								inputProps={registeredInputs?.firstName}
								ref={registeredInputs?.firstName?.ref}
								error={errors.firstName?.message}
							/>
						</div>
						<div style={{ flex: 1 }}>
							<Input
								isIcon={false}
								label="Last name"
								placeholder=""
								inputProps={registeredInputs?.lastName}
								ref={registeredInputs?.lastName?.ref}
								error={errors.lastName?.message}
							/>
						</div>
					</div>
					<div className={styles.numberGroup}>
						<div className={styles.innerGroup}>
							<div className={styles.prefix}>
								<div className={styles.text}>Prefix</div>
								<div className={styles.value}>+234</div>
							</div>
							<Controller
								name="phoneNumber"
								control={control}
								rules={{
									required: "Phone number is required",
									validate: value => {
										const numberWithoutCode = value.replace(/^\+234/, "").replace(/\s/g, "");
										return numberWithoutCode.length === 10 || "Invalid phone number";
									},
								}}
								render={({ field }) => (
									<AddressFormPhone
										label="Phone number"
										error={errors.phoneNumber?.message}
										value={field.value}
										onChange={value => {
											field.onChange(value.replaceAll(' ', ''));
											trigger("phoneNumber"); 
										}}
									/>
								)}
							/>
						</div>
						<div className={styles.innerGroup}>
							<div className={styles.prefix}>
								<div className={styles.text}>Prefix</div>
								<div className={styles.value}>+234</div>
							</div>
							<Controller
								name="additionalPhoneNumber"
								control={control}
								rules={{
									required: false,
									validate: value => {
										if (!value) {
											return true;
										  }
										const numberWithoutCode = value?.replace(/^\+234/, "").replace(/\s/g, "");
										return numberWithoutCode?.length === 10 || "Invalid phone number";
									},
								}}
								render={({ field }) => (
									<AddressFormPhone
										label="Additional Phone number"
										error={errors.additionalPhoneNumber?.message}
										value={field.value??""}
										onChange={value => {
											field.onChange(value.replaceAll(' ', ''));
											// trigger("additionalPhoneNumber");
										}}
									/>
								)}
							/>
						</div>
					</div>
					<Input
						isIcon={false}
						label="Delivery Address"
						placeholder=""
						inputProps={registeredInputs?.deliveryAddress}
						ref={registeredInputs?.deliveryAddress?.ref}
						error={errors.deliveryAddress?.message}
					/>
					<Input
						isIcon={false}
						label="Additional Information"
						placeholder=""
						inputProps={registeredInputs?.additionalInformation}
						ref={registeredInputs?.additionalInformation?.ref}
					/>
					<div className={styles.inputGroup}>
						<Controller
							name="state"
							control={control}
							rules={{
								required: "State is required",
							}}
							render={({ field, fieldState }) => (
								<Select
									label="State"
									items={stateItems}
									selected={field.value}
									placeholder="Select State"
									error={fieldState.error?.message}
									searchable={true}
									onChange={selectedItem => {
										field.onChange(selectedItem);
									}}
								/>
							)}
						/>
						<Input
							isIcon={false}
							label="City"
							placeholder=""
							inputProps={registeredInputs?.city}
							ref={registeredInputs?.city?.ref}
							error={errors.city?.message}
						/>
					</div>
					<div className={styles.buttonGroup}>
						<button onClick={handleCancel} disabled={createLoading || isEditLoading} className={`${styles.button} ${styles.backButton}`} type="button">
							{" "}
							<svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path
									d="M4.79287 9L1.14645 5.35355C0.951184 5.1583 0.951184 4.8417 1.14644 4.64645L4.79287 1"
									stroke="#FF7926"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
							Back
						</button>
						<div className={styles.sideButton}>
							<button onClick={handleCancel} disabled={createLoading || isEditLoading} className={`${styles.button} ${styles.cancelButton}`} type="button">
								Cancel
							</button>
							<button onClick={handleSubmit(onSubmit)} disabled={createLoading || isEditLoading} className={`${styles.button} ${styles.saveButton}`} type="submit">
								Save
								<svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path
										d="M1.20713 9L4.85355 5.35355C5.04882 5.1583 5.04882 4.8417 4.85356 4.64645L1.20713 1"
										stroke="#FF7926"
										strokeWidth="1.5"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							</button>
						</div>
					</div>
				</div>
			</div>
		</CommonDialog>
	);
};
export default AddressForm;
